import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { IReportEmbedConfiguration, models, service, factories } from 'powerbi-client';
import { PowerBIEmbedModule } from 'powerbi-client-angular';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PowerbiService } from '../../services/powerbi-service';
import { AuthenticationOidcService } from '../../services/authentication-oidc-service';
import { MatSnackBar } from '@angular/material/snack-bar';

const groupIdentifier = 'g';
const reportIdentifier = 'r';
const powerBIReportType = 'report';
@Component({
  selector: 'app-report-viewer',
  standalone: true,
  imports: [CommonModule, PowerBIEmbedModule, MatProgressSpinnerModule],
  templateUrl: './report-viewer.html',
  styleUrls: ['./report-viewer.scss'],
})
export class ReportViewer implements OnInit {
  private readonly powerbiService = inject(PowerbiService);
  private readonly route = inject(ActivatedRoute);
  private readonly authenticationOidcService = inject(AuthenticationOidcService);
  private readonly snackBar = inject(MatSnackBar);

  reportConfig: IReportEmbedConfiguration = {
    id: undefined,
    type: powerBIReportType,
    embedUrl: undefined,
    tokenType: models.TokenType.Embed,
    accessToken: undefined,
    viewMode: models.ViewMode.View,
    settings: {
      panes: {
        filters: {
          expanded: false,
          visible: false,
        },
        pageNavigation: {
          visible: false,
        },
      },
    },
  };

  groupId?: string;
  reportId?: string;
  errorMessage: string | null = null;
  powerbi: service.Service;

  constructor() {
    this.powerbi = new service.Service(
      factories.hpmFactory,
      factories.wpmpFactory,
      factories.routerFactory,
    );
  }

  ngOnInit(): void {
    this.snackBar.dismiss();
    this.route.queryParamMap.subscribe((params) => {
      const groupId = params.get(groupIdentifier);
      const reportId = params.get(reportIdentifier);
      if (!groupId || !reportId) {
        this.showError('Report unavailable. Please check your access or try again.');
        return;
      }
      this.groupId = groupId;
      this.reportId = reportId;
      this.authenticationOidcService.getBearerToken().subscribe({
        next: (token) => {
          this.reportConfig = {
            ...this.reportConfig,
            accessToken: token,
          };
          this.fetchEmbedToken();
        },
        error: (err: any) => {
          console.error('Error obtaining bearer token:', err);
          this.showError('Authentication failed. Please login again.');
        },
      });
      this.errorMessage = null;
    });
  }

  fetchEmbedToken(): void {
    if (!this.groupId || !this.reportId) {
      this.showError('Report unavailable. Please check your access or try again.');
      return;
    }
    this.powerbiService.generateEmbedToken(this.groupId, this.reportId).subscribe({
      next: (response: any) => {
        this.reportConfig = {
          ...this.reportConfig,
          embedUrl: response.embedUrl,
          accessToken: response.embedToken,
          id: this.reportId,
        };
        const reportContainer = document.getElementById('report-container');
        if (reportContainer) {
          this.powerbi.embed(reportContainer, this.reportConfig);
        }
      },
      error: (err: any) => {
        console.error('Error fetching embed token:', err);
        this.showError('Failed to load report. Please try again later.');
      },
    });
  }

  /**
   * Displays an error message using the snack bar.
   * @param message The error message to display.
   */
  private showError(message: string): void {
    this.errorMessage = message;
    this.snackBar.open(message, 'Close', {
      panelClass: ['mat-toolbar', 'mat-warn'],
    });
  }
}
